import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Componentes de Infraestructura (Carga estática)
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import WhatsAppButton from './components/WhatsAppButton';
import { Toaster } from './components/ui/toaster';

// Carga diferida (Lazy Loading) para optimizar el rendimiento (LCP)
const HomePage = lazy(() => import('./pages/HomePage'));
const AlergiasPage = lazy(() => import('./pages/AlergiasPage'));
const BiomagnetismoInfantilPage = lazy(() => import('./pages/BiomagnetismoInfantilPage'));
const BiomagnetismoPage = lazy(() => import('./pages/BiomagnetismoPage'));
const CamposMagneticosPage = lazy(() => import('./pages/CamposMagneticosPage'));
const ConsultasOnlinePage = lazy(() => import('./pages/ConsultasOnlinePage'));
const ContactoPage = lazy(() => import('./pages/ContactoPage'));
const FlorDetailPage = lazy(() => import('./pages/FlorDetailPage'));
const FloresBachListPage = lazy(() => import('./pages/FloresBachListPage'));
const FloresBachPage = lazy(() => import('./pages/FloresBachPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const OrigenBiomagnetismoPage = lazy(() => import('./pages/OrigenBiomagnetismoPage'));
const ParBiomagneticoPage = lazy(() => import('./pages/ParBiomagneticoPage'));
const ParesTemporalesPage = lazy(() => import('./pages/ParesTemporalesPage'));
const PrivacidadPage = lazy(() => import('./pages/PrivacidadPage'));

// Nuevas páginas de temas
const OrigenParPage       = lazy(() => import('./pages/OrigenParPage'));
const PhTerrenoPage       = lazy(() => import('./pages/PhTerrenoPage'));
const MapaPuntosPage      = lazy(() => import('./pages/MapaPuntosPage'));
const PuntosReservoriosPage = lazy(() => import('./pages/PuntosReservoriosPage'));
const BioshockPage        = lazy(() => import('./pages/BioshockPage'));
const PnlPage             = lazy(() => import('./pages/PnlPage'));
const FenomenoTumoralPage = lazy(() => import('./pages/FenomenoTumoralPage'));
const PsicoEmocionalPage  = lazy(() => import('./pages/PsicoEmocionalPage'));
const ProtocoloSesionPage = lazy(() => import('./pages/ProtocoloSesionPage'));
const ProtocoloDetoxPage  = lazy(() => import('./pages/ProtocoloDetoxPage'));
const ImanesPage          = lazy(() => import('./pages/ImanesPage'));
const EbooksPage          = lazy(() => import('./pages/EbooksPage'));
const GuiaEstresPage           = lazy(() => import('./pages/GuiaEstresPage'));
const GuiaContracturasPage     = lazy(() => import('./pages/GuiaContracturasPage'));
const GuiasPage                = lazy(() => import('./pages/GuiasPage'));
const ProductPage              = lazy(() => import('./pages/infoproductos/ProductPage'));

// Dispara PageView en cada cambio de ruta de React Router
function PixelPageView() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);
  return null;
}

// Pantalla de carga ultraligera
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <Router>
      <PixelPageView />
      <ScrollToTop />
      {/* El componente Layout ya envuelve Navbar, Footer y las flechas de navegación */}
      <Layout> 
        <main className="outline-none">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              
              {/* Sección Biomagnetismo */}
              <Route path="/biomagnetismo" element={<BiomagnetismoPage />} />
              <Route path="/par-biomagnetico" element={<ParBiomagneticoPage />} />
              <Route path="/pares-temporales" element={<ParesTemporalesPage />} />
              <Route path="/alergias" element={<AlergiasPage />} />
              <Route path="/origen-biomagnetismo" element={<OrigenBiomagnetismoPage />} />
              <Route path="/biomagnetismo-infantil" element={<BiomagnetismoInfantilPage />} />
              <Route path="/campos-magneticos" element={<CamposMagneticosPage />} />

              {/* Nuevas páginas de temas */}
              <Route path="/origen-par-biomagnetico"    element={<OrigenParPage />} />
              <Route path="/ph-terreno-biologico"       element={<PhTerrenoPage />} />
              <Route path="/mapa-puntos-biomagneticos"  element={<MapaPuntosPage />} />
              <Route path="/puntos-reservorios"         element={<PuntosReservoriosPage />} />
              <Route path="/conflicto-biologico"        element={<BioshockPage />} />
              <Route path="/pnl-reequilibrio-emocional" element={<PnlPage />} />
              <Route path="/fenomeno-tumoral"           element={<FenomenoTumoralPage />} />
              <Route path="/pares-psicoemocionales"     element={<PsicoEmocionalPage />} />
              <Route path="/protocolo-sesion"           element={<ProtocoloSesionPage />} />
              <Route path="/protocolo-detox"            element={<ProtocoloDetoxPage />} />
              <Route path="/imanes"                      element={<ImanesPage />} />
              <Route path="/ebooks" element={<Navigate to="/guia-estres" replace />} />

              {/* Landing premium para el ebook principal */}
              <Route path="/guia-estres" element={<GuiaEstresPage />} />
              {/* Nuevas landings de guías */}
              <Route path="/guia-contracturas" element={<GuiaContracturasPage />} />
              <Route path="/guias"             element={<GuiasPage />} />
              {/* Guías / Infoproductos — landing dinámica por slug (catch-all) */}
              <Route path="/guia-:slug" element={<ProductPage />} />
              {/* Redirect URL amigable → canonical */}
              <Route path="/infoproductos/guia-estres-insomnio" element={<Navigate to="/guia-estres" replace />} />
              {/* Redirect legacy: mantener compatibilidad con URLs anteriores */}
              <Route path="/infoproductos/:slug" element={<ProductPage />} />

              {/* Sección Flores de Bach */}
              <Route path="/flores-bach" element={<FloresBachPage />} />
              <Route path="/catalogo-flores" element={<FloresBachListPage />} />
              <Route path="/flores-de-bach/:id" element={<FlorDetailPage />} />
              <Route path="/consultas-online" element={<ConsultasOnlinePage />} />

              {/* Páginas adicionales */}
              <Route path="/contacto" element={<ContactoPage />} />
              <Route path="/privacidad" element={<PrivacidadPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
      </Layout>
      
      {/* Elementos flotantes globales */}
      <WhatsAppButton />
      <Toaster />
    </Router>
  );
}

export default App;